if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
      }

      setActiveMedia(mediaId, smooth = false) {
        if (!mediaId) return false;

        let cleanMediaId = mediaId;
        if (mediaId.indexOf('-') !== -1) {
          const parts = mediaId.split('-');
          cleanMediaId = parts[parts.length - 1];
        }

        let element = this.querySelector(`[data-media-id="${cleanMediaId}"]`);
        
        if (!element) {
          element = this.querySelector(`[data-media-id="${mediaId}"]`);
        }

        if (!element) return false;

        return this._activateMediaElement(element, smooth);
      }

      _activateMediaElement(element, smooth = false) {
        if (!element) return false;

        this.querySelectorAll('[data-media-id].active').forEach((node) => node.classList.remove('active'));

        element.classList.add('active');

        const allMediaElements = Array.from(this.querySelectorAll('[data-media-id]'));
        const mediaIndex = allMediaElements.indexOf(element);

        this.syncSlideshow(element, mediaIndex, smooth);
        this.syncThumbnails(element, mediaIndex);

        if (
          this.classList.contains('media-gallery--grid') &&
          !this.classList.contains('media-gallery--product-only')
        ) {
          this.swapGridMedia(element, mediaIndex);
        }

        return true;
      }

      /**
       * Append new variant media into the existing carousel without recreating
       * the swiper-component. Used as a fast path when the active gallery is
       * filtered by variant (`hide_variants`) and the requested media is not
       * yet present in the DOM. Avoids a full Swiper re-init that on
       * high-cardinality products (80+ slides) costs 20-50s on mid-tier mobile.
       *
       * Returns true on success, false if the operation can't be performed
       * (caller should fall back to the legacy `replaceWith` path).
       */
      appendVariantMedia(newGalleryRoot, variantFeaturedMediaId) {
        try {
          if (!newGalleryRoot || !variantFeaturedMediaId) return false;
          if (!this.classList.contains('media-gallery--carousel')) return false;

          const swiperComponent = this.querySelector('swiper-component');
          const mainSwiper = swiperComponent?.initSwiper;
          if (!swiperComponent || !mainSwiper || typeof mainSwiper.update !== 'function') return false;

          const mainWrapper = this.querySelector('.swiper-main .swiper-wrapper');
          if (!mainWrapper) return false;

          const newMediaGallery = newGalleryRoot.matches?.('media-gallery')
            ? newGalleryRoot
            : newGalleryRoot.querySelector('media-gallery');
          if (!newMediaGallery) return false;

          const newMainWrapper = newMediaGallery.querySelector('.swiper-main .swiper-wrapper');
          if (!newMainWrapper) return false;

          const featuredIdStr = String(variantFeaturedMediaId);
          const existingIds = new Set(
            Array.from(mainWrapper.querySelectorAll('.swiper-slide [data-media-id]'))
              .map((el) => el.getAttribute('data-media-id'))
              .filter(Boolean)
          );

          if (existingIds.has(featuredIdStr)) return false;

          const newSlides = Array.from(newMainWrapper.querySelectorAll(':scope > .swiper-slide'));
          if (!newSlides.length) return false;

          const slidesToAppend = [];
          newSlides.forEach((slide) => {
            const dataMediaEl = slide.querySelector('[data-media-id]');
            const mediaId = dataMediaEl?.getAttribute('data-media-id');
            if (mediaId && !existingIds.has(mediaId)) {
              slidesToAppend.push({ slide, mediaId, newIndex: newSlides.indexOf(slide) });
            }
          });

          if (!slidesToAppend.length) return false;
          if (!slidesToAppend.some(({ mediaId }) => mediaId === featuredIdStr)) return false;

          const newThumbsContainer = newMediaGallery.querySelector('.swiper-controls__thumbnails-container');
          const newThumbButtons = newThumbsContainer
            ? Array.from(newThumbsContainer.querySelectorAll('.swiper-controls__thumbnail'))
            : [];

          const staticThumbsContainer = this.querySelector(
            '.swiper-controls__thumbnails-container[data-static-thumbnails]'
          );
          const staticThumbsRow = staticThumbsContainer?.querySelector(
            '.swiper-controls__thumbnails-static-row'
          );
          // Products below the static-thumbnails threshold still expose a
          // thumb strip, but it lives inside the regular swiper-based
          // .swiper-wrapper. Without appending here the freshly added
          // variant slide remains unreachable from the thumbs and the
          // active class never propagates after a variant change.
          const swiperThumbsWrapper = !staticThumbsRow
            ? this.querySelector(
                '.swiper-controls__thumbnails-container .swiper-wrapper.swiper-controls__thumbnails'
              )
            : null;
          const thumbsAppendTarget = staticThumbsRow || swiperThumbsWrapper;

          const appendedSlides = [];
          const appendedZoomIndices = [];
          slidesToAppend.forEach(({ slide, newIndex }) => {
            const slideClone = slide.cloneNode(true);
            slideClone.classList.remove(
              'swiper-slide-active',
              'swiper-slide-next',
              'swiper-slide-prev',
              'swiper-slide-visible',
              'swiper-slide-fully-visible'
            );
            slideClone.style.removeProperty('width');
            slideClone.style.removeProperty('margin-right');

            // Re-index zoom triggers for the dialog. The variant response renders
            // a hide_variants gallery containing only the current variant's media,
            // so every slide arrives with data-zoom-index="0". We need to align it
            // with the actual position the slide will occupy in the dialog gallery.
            const targetZoomIndex = mainWrapper.children.length;
            this._reindexZoomAttribute(slideClone, targetZoomIndex);
            appendedZoomIndices.push(targetZoomIndex);

            mainWrapper.appendChild(slideClone);
            appendedSlides.push(slideClone);

            if (thumbsAppendTarget && newThumbButtons.length) {
              const sourceThumb = newThumbButtons[newIndex];
              if (sourceThumb) {
                const thumbClone = sourceThumb.cloneNode(true);
                thumbClone.classList.remove(
                  'active',
                  'swiper-slide-active',
                  'swiper-slide-thumb-active',
                  'swiper-slide-next',
                  'swiper-slide-prev',
                  'swiper-slide-visible',
                  'swiper-slide-fully-visible'
                );
                thumbClone.style.removeProperty('width');
                thumbClone.style.removeProperty('margin-right');
                // Let Swiper recompute slide bookkeeping on update().
                thumbClone.removeAttribute('data-swiper-slide-index');
                const nextIndex = thumbsAppendTarget.querySelectorAll(
                  '.swiper-controls__thumbnail'
                ).length;
                thumbClone.setAttribute('data-index', String(nextIndex));
                thumbClone.setAttribute('aria-label', `Slide ${nextIndex + 1}`);
                this._reindexZoomAttribute(thumbClone, targetZoomIndex);
                thumbsAppendTarget.appendChild(thumbClone);
              }
            }
          });

          mainSwiper.update();

          if (staticThumbsContainer && typeof swiperComponent._attachStaticThumbnailListeners === 'function') {
            swiperComponent._attachStaticThumbnailListeners();
          } else if (swiperThumbsWrapper) {
            // Tell the linked thumbs swiper about the newly inserted slides
            // so its .slides array and active class machinery stay in sync
            // with the main carousel.
            if (swiperComponent._thumbsSwiper && !swiperComponent._thumbsSwiper.destroyed) {
              swiperComponent._thumbsSwiper.update();
            }
            if (typeof swiperComponent._attachSwiperThumbnailListeners === 'function') {
              swiperComponent._attachSwiperThumbnailListeners();
            }
          }

          // Propagate the new variant media to the zoom dialog (both the lazy
          // <template data-zoom-content> form and the already-mounted form).
          // Without this the dialog still shows the original media when opened
          // after a variant change on `hide_variants` products.
          this._appendVariantMediaToZoomDialog(
            newMediaGallery,
            slidesToAppend.map(({ mediaId }, i) => ({
              mediaId,
              zoomIndex: appendedZoomIndices[i],
            }))
          );

          const newFeaturedElement =
            this.querySelector(`[data-media-id="${featuredIdStr}"]`);
          if (newFeaturedElement) {
            this._activateMediaElement(newFeaturedElement, true);
          }

          this.dispatchEvent(
            new CustomEvent('media-gallery:variant-media-appended', {
              bubbles: true,
              detail: { count: appendedSlides.length, featuredMediaId: featuredIdStr },
            })
          );

          return true;
        } catch (e) {
          return false;
        }
      }

      _reindexZoomAttribute(root, newIndex) {
        if (!root) return;
        const value = String(newIndex);
        if (root.hasAttribute && root.hasAttribute('data-zoom-index')) {
          root.setAttribute('data-zoom-index', value);
        }
        root.querySelectorAll?.('[data-zoom-index]').forEach((el) => {
          el.setAttribute('data-zoom-index', value);
        });
      }

      _getZoomDialogSources(zoomDialog) {
        if (!zoomDialog) return null;
        const template = zoomDialog.querySelector(':scope > template[data-zoom-content]');
        const root = template ? template.content : zoomDialog;
        return {
          template,
          isLazy: !!template,
          zoomedGallery: root.querySelector('.dialog-zoomed-gallery'),
          thumbsList: root.querySelector('.dialog-thumbnails-list'),
        };
      }

      _appendVariantMediaToZoomDialog(newMediaGallery, entries) {
        try {
          if (!Array.isArray(entries) || !entries.length) return;

          const currentZoomDialog = this.querySelector('zoom-dialog');
          if (!currentZoomDialog) return;
          const currentSources = this._getZoomDialogSources(currentZoomDialog);
          if (!currentSources || !currentSources.zoomedGallery) return;

          const newZoomDialog = newMediaGallery.querySelector('zoom-dialog');
          const newSources = this._getZoomDialogSources(newZoomDialog);
          if (!newSources || !newSources.zoomedGallery) return;

          entries.forEach(({ mediaId, zoomIndex }) => {
            const inner = newSources.zoomedGallery.querySelector(`[data-media-id="${mediaId}"]`);
            const sourceMediaContainer = inner?.closest('.product-media-container');
            if (sourceMediaContainer && !currentSources.zoomedGallery.querySelector(`#${CSS.escape(sourceMediaContainer.id || '')}`)) {
              const containerClone = sourceMediaContainer.cloneNode(true);
              this._reindexZoomAttribute(containerClone, zoomIndex);
              currentSources.zoomedGallery.appendChild(containerClone);
            }

            if (currentSources.thumbsList) {
              let thumbClone = newSources.thumbsList?.querySelector(
                `.dialog-thumbnails-list__thumbnail [data-media-id="${mediaId}"]`
              )?.closest('.dialog-thumbnails-list__thumbnail')?.cloneNode(true);

              // Variant responses for products whose sorted_media.size is 1
              // skip the {% for %} that renders the dialog thumbnails-list,
              // so the new <button> isn't available in newSources. Build a
              // synthetic thumb by cloning an existing template thumb and
              // patching its <img> from the freshly appended media.
              if (!thumbClone && currentSources.thumbsList.firstElementChild && sourceMediaContainer) {
                thumbClone = currentSources.thumbsList.firstElementChild.cloneNode(true);
                const newImg = sourceMediaContainer.querySelector('img');
                const baseImg = thumbClone.querySelector('img');
                if (newImg && baseImg) {
                  if (newImg.getAttribute('src')) baseImg.setAttribute('src', newImg.getAttribute('src'));
                  if (newImg.getAttribute('srcset')) baseImg.setAttribute('srcset', newImg.getAttribute('srcset'));
                  if (newImg.getAttribute('alt')) baseImg.setAttribute('alt', newImg.getAttribute('alt'));
                  baseImg.removeAttribute('data-src');
                  baseImg.removeAttribute('data-srcset');
                }
                const newAlt = sourceMediaContainer.querySelector('img')?.getAttribute('alt');
                if (newAlt) {
                  thumbClone.setAttribute('aria-label', newAlt);
                }
                thumbClone.removeAttribute('aria-selected');
              }

              if (thumbClone) {
                this._reindexZoomAttribute(thumbClone, zoomIndex);
                thumbClone.removeAttribute('aria-selected');
                currentSources.thumbsList.appendChild(thumbClone);
              }
            }
          });

          // If the dialog is already mounted (user opened it before changing
          // the variant), refresh its internal media snapshot so the freshly
          // appended <li> entries become reachable via this.media[index].
          // Without this, open(index) for the variant slide receives an
          // undefined targetImage and the dialog falls back to the first
          // media. The template/lazy branch needs no refresh because
          // _ensureMounted() re-captures refs the first time it runs.
          if (!currentSources.isLazy && typeof currentZoomDialog._refreshMediaRefs === 'function') {
            currentZoomDialog._refreshMediaRefs();
          }
        } catch (e) {
          // silent
        }
      }

      /**
       * Sync slideshow/swiper component when media is activated
       * @param {HTMLElement} mediaElement - The active media element
       * @param {number} mediaIndex - The index of the media in the gallery
       * @param {boolean} smooth - Whether to use smooth transition
       */
      syncSlideshow(mediaElement, mediaIndex, smooth = false) {
        try {
          if (!this.classList.contains('media-gallery--carousel')) return;

          const swiperComponent = this.querySelector('swiper-component');
          if (!swiperComponent) return;
          
          const waitForSwiper = (retries = 10) => {
            if (swiperComponent.initSwiper) {
              const slides = Array.from(swiperComponent.querySelectorAll('.swiper-wrapper .swiper-slide'));
              let slideIndex = mediaIndex;
              const slideElement = mediaElement.closest('.swiper-slide');
              if (slideElement) {
                const foundIndex = slides.indexOf(slideElement);
                if (foundIndex >= 0) {
                  slideIndex = foundIndex;
                }
              }

              if (slideIndex >= 0 && slideIndex < slides.length && swiperComponent.initSwiper) {
                swiperComponent.initSwiper.slideTo(slideIndex, smooth ? 800 : 0);
              }
            } else if (retries > 0) {
              setTimeout(() => waitForSwiper(retries - 1), 50);
            }
          };

          waitForSwiper();
        } catch (e) {
          try {
            if (this.slideshow && typeof this.slideshow.goTo === 'function') {
              this.slideshow.goTo(mediaIndex);
            }
          } catch (err) {
            // silent
          }
        }
      }

      /**
       * Sync thumbnails controls when media is activated
       * @param {HTMLElement} mediaElement - The active media element
       * @param {number} mediaIndex - The index of the media in the gallery
       */
      syncThumbnails(mediaElement, mediaIndex) {
        try {
          const thumbnailsContainer = this.querySelector('.swiper-controls__thumbnails-container');
          if (!thumbnailsContainer) return;

          const thumbnailButtons = Array.from(thumbnailsContainer.querySelectorAll('.swiper-controls__thumbnail[data-index]'));
          if (thumbnailButtons.length === 0) return;

          let slideIndex = mediaIndex;
          
          if (this.classList.contains('media-gallery--carousel')) {
            const swiperComponent = this.querySelector('swiper-component');
            if (swiperComponent) {
              const slideElement = mediaElement.closest('.swiper-slide');
              if (slideElement) {
                const slides = Array.from(swiperComponent.querySelectorAll('.swiper-wrapper .swiper-slide'));
                const foundIndex = slides.indexOf(slideElement);
                if (foundIndex >= 0) {
                  slideIndex = foundIndex;
                }
              } else {
                if (swiperComponent.initSwiper && typeof swiperComponent.initSwiper.activeIndex === 'number') {
                  slideIndex = mediaIndex;
                }
              }
            }
          }

          let targetThumbnail = thumbnailButtons.find((button) => {
            const index = parseInt(button.getAttribute('data-index'), 10);
            return !isNaN(index) && index === slideIndex;
          });

          if (!targetThumbnail) {
            targetThumbnail = thumbnailButtons.find((button) => {
              const index = parseInt(button.getAttribute('data-index'), 10);
              return !isNaN(index) && index === mediaIndex;
            });
          }

          if (targetThumbnail) {
            const finalSlideIndex = parseInt(targetThumbnail.getAttribute('data-index'), 10);

            this.syncThumbnailSwiper(thumbnailsContainer, thumbnailButtons, targetThumbnail, finalSlideIndex);
          }
        } catch (e) {
          // silent
        }
      }

      /**
       * Sync thumbnail swiper to show the active thumbnail
       * @param {HTMLElement} thumbnailsContainer - The thumbnail container element
       * @param {Array} thumbnailButtons - Array of all thumbnail buttons
       * @param {HTMLElement} targetThumbnail - The active thumbnail button
       * @param {number} slideIndex - The slide index to sync to
       */
      syncThumbnailSwiper(thumbnailsContainer, thumbnailButtons, targetThumbnail, slideIndex) {
        try {
          const waitForThumbnailSwiper = (retries = 10) => {
            const swiperComponent = this.querySelector('swiper-component');
            let thumbsSwiper = null;

            if (swiperComponent && swiperComponent.initSwiper && swiperComponent.initSwiper.params && swiperComponent.initSwiper.params.thumbs) {
              thumbsSwiper = swiperComponent.initSwiper.params.thumbs.swiper;
            }

            if (!thumbsSwiper) {
              const thumbnailSwiper = thumbnailsContainer.querySelector('.swiper');
              if (thumbnailSwiper && thumbnailSwiper.swiper) {
                thumbsSwiper = thumbnailSwiper.swiper;
              }
            }

            if (thumbsSwiper) {
              if (slideIndex >= 0 && slideIndex < thumbnailButtons.length) {
                thumbsSwiper.slideTo(slideIndex, 300);
              }
            } else if (retries > 0) {
              setTimeout(() => waitForThumbnailSwiper(retries - 1), 50);
            }
          };

          waitForThumbnailSwiper();
        } catch (e) {
          // silent
        }
      }

      /**
       * Swap media in grid layout: move variant image to first position
       * @param {HTMLElement} mediaElement - The active media element
       * @param {number} mediaIndex - The index of the media in the gallery
       */
      swapGridMedia() {
        if (this.classList.contains('media-gallery--product-only')) return;

        try {
          if (typeof MainEvents !== 'undefined' && MainEvents.variantUpdate) {
            const handleVariantUpdateOnce = (event) => {
              try {
                const source = event?.detail?.data?.html;
                if (!source) return;

                const newMediaGallery = source.querySelector('media-gallery');
                if (!newMediaGallery) return;
                this.replaceWith(newMediaGallery);

                try {
                  newMediaGallery.scrollIntoView({ behavior: 'smooth' });
                } catch (e) {
                  // ignore scrolling errors
                }
              } catch (e) {
                // silent
              } finally {
                document.removeEventListener(MainEvents.variantUpdate, handleVariantUpdateOnce);
              }
            };
            document.addEventListener(MainEvents.variantUpdate, handleVariantUpdateOnce);
          }
          
        } catch (e) {
          // silent
        }
      }
    }
  );
}